import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import {
  DataGridPremium,
  type GridApi,
  type GridColDef,
  type GridFilterModel,
  type GridFilterOperator,
  type GridPaginationModel,
} from '@mui/x-data-grid-premium'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField'
import type { Invoice, InvoiceState, InvoiceType } from '@mynt/backoffice-api-spec/invoice'
import dayjs, { type Dayjs } from 'dayjs'
import { useCallback, useMemo, useRef, useState } from 'react'
import Spacings from '../../figma/tokens/Spacings'
import { prettifyEnumLabel } from '../../helpers/CreditOnCardHelpers'
import FigmaBox from '../../mynt-components/components/FigmaBox'
import { YYYY_MM_DD } from '../../tiger/Constants'
import InvoiceDetails from './InvoiceDetails'
import { DEFAULT_FILTERS, type InvoiceTableFilters } from './types'

export type InvoiceTableProps = {
  invoices: Invoice[]
  filters: InvoiceTableFilters
  onFilterChange: (filters: InvoiceTableFilters) => void
  isLoading?: boolean
  actions: (invoice: Invoice, onClose?: () => unknown) => React.ReactNode
  formatAmount?: (amount: { value: number; currency: string }) => React.ReactNode | string
  page: 'ACCOUNTING' | 'CUSTOMER'
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
  rowCount: number
}

const noop = () => null

const INVOICE_STATES: Record<string, string> = {
  DRAFT: 'DRAFT',
  BOOKED: 'BOOKED',
  SETTLED: 'SETTLED',
  CANCELLED: 'CANCELLED',
  OVER_DUE: 'OVER_DUE',
  REMINDED: 'REMINDED',
  REMINDER_OVERDUE: 'REMINDER_OVERDUE',
  DEBT_COLLECTION: 'DEBT_COLLECTION',
  CREDITED: 'CREDITED',
}

const INVOICE_TYPES: Record<string, string> = {
  REQUEST_FOR_PAYMENT: 'REQUEST_FOR_PAYMENT',
  REPAYMENT_INVOICE: 'REPAYMENT_INVOICE',
  SUBSCRIPTION_FEE: 'SUBSCRIPTION_FEE',
  ADMINISTRATION_FEE: 'ADMINISTRATION_FEE',
  WITHDRAWAL_TO_MYNT: 'WITHDRAWAL_TO_MYNT',
  WITHDRAWAL_TO_CUSTOMER: 'WITHDRAWAL_TO_CUSTOMER',
  CREDIT_NOTE: 'CREDIT_NOTE',
  CREDIT_LIMIT_FEE: 'CREDIT_LIMIT_FEE',
  CARD_CREDIT_INTEREST_FEE: 'CARD_CREDIT_INTEREST_FEE',
  CARD_CREDIT_DELAYED_PAYMENT: 'CARD_CREDIT_DELAYED_PAYMENT',
  INVOICE_FEE: 'INVOICE_FEE',
  CASHBACK: 'CASHBACK',
  CARD_CREDIT_MONTHLY_FEE: 'CARD_CREDIT_MONTHLY_FEE',
  CARD_CREDIT_BILLING_FEE: 'CARD_CREDIT_BILLING_FEE',
  CARD_CREDIT_USAGE_FEE: 'CARD_CREDIT_USAGE_FEE',
}

const CURRENCIES: Record<string, string> = {
  SEK: 'SEK',
  NOK: 'NOK',
  EUR: 'EUR',
  DKK: 'DKK',
}

const createSelectOperator = (label: string, options: Record<string, string>, operator: string): GridFilterOperator => ({
  label: label,
  value: operator,
  getApplyFilterFn: noop,
  InputComponent: ({ applyValue, clearButton, ...params }) => {
    const selected = (params.item.value as string[] | undefined) ?? []
    const labelId = `invoice-filter-${params.item.field}`

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>
        <FormControl fullWidth sx={{ minWidth: 0 }}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            labelId={labelId}
            label={label}
            multiple
            value={selected}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => {
              const value = event.target.value
              applyValue({ ...params.item, value: typeof value === 'string' ? value.split(',') : value })
            }}
            renderValue={(values) => (values.length === 0 ? '' : `${values.length} selected`)}
            sx={{
              // Theme sets `.MuiSelect-multiple` to 8px padding, which is shorter than the
              // 12.5px outlined inputs and drops the label and value below the rest of the row.
              '&& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                padding: '12.5px 32px 12.5px 12.5px',
              },
            }}
          >
            {Object.values(options).map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selected.includes(option)} size="small" sx={{ pointerEvents: 'none', padding: 0, marginRight: 1 }} />
                {prettifyEnumLabel(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selected.length > 0 ? clearButton : null}
      </Box>
    )
  },
  headerLabel: label,
})

const TextFilterInput = ({ applyValue, ...params }: { applyValue: (value: any) => void; item: any; label?: string }) => {
  // Search on blur (or Enter) rather than on every keystroke, to avoid a request per character.
  const commit = (value: string) => {
    if (value === (params.item.value ?? '')) return
    applyValue({ ...params.item, value })
  }

  return (
    <Box sx={{ width: '100%', paddingTop: 2 }}>
      <TextField
        fullWidth
        label={params.label}
        defaultValue={params.item.value ?? ''}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter') commit((e.target as HTMLInputElement).value)
        }}
      />
    </Box>
  )
}

const createTextFilterOperator = (label: string, operator: string): GridFilterOperator => ({
  label,
  value: operator,
  getApplyFilterFn: noop,
  InputComponent: TextFilterInput,
  headerLabel: label,
})

const NumberRangeInput = ({ applyValue, ...params }: { applyValue: (value: any) => void; item: any; label?: string }) => {
  const value = (params.item.value as [number | undefined, number | undefined]) ?? [undefined, undefined]
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  const itemRef = useRef(params.item)
  itemRef.current = params.item

  const handleChange = (index: 0 | 1) => (event: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(timerRef.current)
    const num = event.target.value ? Number(event.target.value) : undefined
    const newValue: [number | undefined, number | undefined] = [...value] as [number | undefined, number | undefined]
    newValue[index] = num
    timerRef.current = setTimeout(() => {
      applyValue({ ...itemRef.current, value: newValue })
    }, 300)
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, width: '100%', paddingTop: 2 }}>
      <TextField
        type="number"
        label="Min"
        defaultValue={value[0] ?? ''}
        onChange={handleChange(0)}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <TextField
        type="number"
        label="Max"
        defaultValue={value[1] ?? ''}
        onChange={handleChange(1)}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </Box>
  )
}

const createNumberRangeOperator = (label: string, operator: string): GridFilterOperator => ({
  label,
  value: operator,
  getApplyFilterFn: noop,
  InputComponent: NumberRangeInput,
  headerLabel: label,
})

const invoiceNoFilterOperators = [createTextFilterOperator('Invoice #', 'customText')]
const receiverFilterOperators = [createTextFilterOperator('Receiver', 'customText')]
const stateFilterOperators = [createSelectOperator('Any of', INVOICE_STATES, 'customAnyOf')]
const grossTotalFilterOperators = [createNumberRangeOperator('Amount range', 'customNumberRange')]
const typeFilterOperators = [createSelectOperator('Any of', INVOICE_TYPES, 'customType')]
const currencyFilterOperators = [createSelectOperator('Any of', CURRENCIES, 'customCurrency')]
const dateRangeFilterOperator: GridFilterOperator[] = [
  {
    label: 'Date range',
    value: 'customDateRange',
    getApplyFilterFn: noop,
    InputComponent: ({ applyValue, ...params }) => {
      const [liveValue, setLiveValue] = useState(params.item.value)

      return (
        <Box sx={{ width: '100%', paddingTop: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateRangePicker
              value={liveValue}
              format="YYYY-MM-DD"
              label=""
              slots={{ field: SingleInputDateRangeField }}
              onChange={setLiveValue}
              onAccept={(value) => applyValue({ ...params.item, value })}
              slotProps={{
                actionBar: { actions: ['clear'] },
                textField: { fullWidth: true },
                openPickerButton: { sx: { marginRight: Spacings.minimum } },
                field: { 'data-testid': `date-range-${params.item.field}` } as any,
              }}
            />
          </LocalizationProvider>
        </Box>
      )
    },
    headerLabel: 'Date range',
  },
]

const applyUpdatedFilters = (model: GridFilterModel): InvoiceTableFilters =>
  model.items.reduce<InvoiceTableFilters>((acc, item) => {
    switch (item.field) {
      case 'state':
        return { ...acc, state: (item.value as InvoiceState[]) ?? [] }
      case 'type':
        return { ...acc, type: (item.value as InvoiceType[]) ?? [] }
      case 'invoiceDate': {
        const [from, to] = (item.value as [Dayjs | null, Dayjs | null]) ?? [null, null]
        return { ...acc, invoiceDate: { from: from ?? undefined, to: to ?? undefined } }
      }
      case 'settleDate': {
        const [from, to] = (item.value as [Dayjs | null, Dayjs | null]) ?? [null, null]
        return { ...acc, settleDate: { from: from ?? undefined, to: to ?? undefined } }
      }
      case 'dueDate': {
        const [from, to] = (item.value as [Dayjs | null, Dayjs | null]) ?? [null, null]
        return { ...acc, dueDate: { from: from ?? undefined, to: to ?? undefined } }
      }
      case 'receiverName':
        return { ...acc, receiverName: (item.value as string) ?? '' }
      case 'no':
        return { ...acc, invoiceNo: (item.value as string) ?? '' }
      case 'grossTotal.currency':
        return { ...acc, currency: (item.value as string[]) ?? [] }
      case 'grossTotal.value': {
        const [min, max] = (item.value as [number | undefined, number | undefined]) ?? [undefined, undefined]
        return { ...acc, grossTotalMin: min, grossTotalMax: max }
      }
      default:
        return acc
    }
  }, DEFAULT_FILTERS)

export const InvoiceTable = ({
  invoices,
  filters,
  onFilterChange,
  isLoading,
  actions,
  formatAmount,
  page,
  paginationModel,
  onPaginationModelChange,
  rowCount,
}: InvoiceTableProps) => {
  const apiRef = useRef<GridApi>({} as GridApi)
  const actionsRef = useRef(actions)
  actionsRef.current = actions
  const formatAmountRef = useRef(formatAmount)
  formatAmountRef.current = formatAmount
  const handleFilterChange = useCallback(
    (model: GridFilterModel, details) => {
      if (!details.reason) return
      const updatedFilters = applyUpdatedFilters(model)
      onFilterChange(updatedFilters)
    },
    [onFilterChange],
  )

  const filterModel = useMemo<GridFilterModel>(
    () => ({
      items: [
        {
          id: 1,
          field: 'state',
          operator: 'customAnyOf',
          value: filters.state.length > 0 ? filters.state : undefined,
        },
        {
          id: 2,
          field: 'type',
          operator: 'customType',
          value: filters.type,
        },
        {
          id: 3,
          field: 'invoiceDate',
          operator: 'customDateRange',
          value: [
            filters.invoiceDate.from ? dayjs(filters.invoiceDate.from) : null,
            filters.invoiceDate.to ? dayjs(filters.invoiceDate.to) : null,
          ],
        },
        {
          id: 4,
          field: 'settleDate',
          operator: 'customDateRange',
          value: [
            filters.settleDate.from ? dayjs(filters.settleDate.from) : null,
            filters.settleDate.to ? dayjs(filters.settleDate.to) : null,
          ],
        },
        {
          id: 5,
          field: 'dueDate',
          operator: 'customDateRange',
          value: [filters.dueDate.from ? dayjs(filters.dueDate.from) : null, filters.dueDate.to ? dayjs(filters.dueDate.to) : null],
        },
        {
          id: 6,
          field: 'receiverName',
          operator: 'customText',
          value: filters.receiverName,
        },
        {
          id: 7,
          field: 'no',
          operator: 'customText',
          value: filters.invoiceNo,
        },
        {
          id: 8,
          field: 'grossTotal.currency',
          operator: 'customCurrency',
          value: filters.currency,
        },
        {
          id: 9,
          field: 'grossTotal.value',
          operator: 'customNumberRange',
          value: [filters.grossTotalMin, filters.grossTotalMax],
        },
      ],
    }),
    [filters],
  )

  const columns = useMemo<GridColDef[]>(
    () =>
      [
        {
          field: 'no',
          headerName: 'Invoice #',
          filterOperators: invoiceNoFilterOperators,
        },
        {
          field: 'companyName',
          headerName: 'Customer',
          width: 200,
          filterable: false,
        },
        {
          field: 'receiverName',
          headerName: 'Receiver',
          width: 200,
          filterOperators: receiverFilterOperators,
        },
        {
          field: 'state',
          headerName: 'State',
          minWidth: 180,
          filterOperators: stateFilterOperators,
          valueFormatter: (value) => prettifyEnumLabel(value),
        },
        {
          field: 'grossTotal.value',
          headerName: 'Gross total',
          valueGetter: (_value, row) => row.grossTotal.value,
          renderCell: ({ row }) => formatAmountRef.current?.(row.grossTotal) ?? row.grossTotal.value,
          width: 150,
          filterOperators: grossTotalFilterOperators,
        },
        {
          field: 'invoiceDate',
          headerName: 'Invoice date',
          type: 'date' as const,
          valueFormatter: (value) => dayjs(value).format(YYYY_MM_DD),
          valueGetter: (value) => dayjs(value).toDate(),
          filterOperators: dateRangeFilterOperator,
          // Deliberately not widened to fit the header filter's full "YYYY-MM-DD – YYYY-MM-DD"
          // range picker without clipping (that needs ~330px): keeping the table's overall width
          // manageable without a horizontal scrollbar to reach Actions matters more here than the
          // filter control rendering uncropped. The cell's own single-date value is unaffected.
          minWidth: 150,
        },
        {
          field: 'dueDate',
          headerName: 'Due date',
          valueGetter: (value) => dayjs(value).toDate(),
          valueFormatter: (value) => dayjs(value).format(YYYY_MM_DD),
          minWidth: 150,
          type: 'date' as const,
          filterOperators: dateRangeFilterOperator,
        },
        {
          field: 'settleDate',
          headerName: 'Settle date',
          type: 'date' as const,
          valueFormatter: (value) => (value ? dayjs(value).format(YYYY_MM_DD) : ''),
          filterOperators: dateRangeFilterOperator,
          minWidth: 150,
        },
        {
          field: 'type',
          headerName: 'Type',
          minWidth: 250,
          filterOperators: typeFilterOperators,
          valueFormatter: (value) => prettifyEnumLabel(value),
        },
        {
          field: 'remaining',
          headerName: 'Remaining',
          valueGetter: (_value, row) => row.remaining.value,
          renderCell: ({ row }) => formatAmountRef.current?.(row.remaining) ?? row.remaining.value,
          width: 150,
          filterable: false,
        },
        {
          field: 'grossTotal.currency',
          headerName: 'Currency',
          valueGetter: (_value, row) => row.grossTotal.currency,
          filterOperators: currencyFilterOperators,
        },
        {
          field: '',
          headerName: 'Actions',
          filterable: false,
          renderCell: ({ row }) => <ActionsColumn actions={actionsRef.current} invoice={row} />,
        },
      ].filter((column) => {
        if (page === 'CUSTOMER') {
          if (column.field === 'companyName') return false
          if (column.field === 'grossTotal.currency') return false
        }
        return true
      }),
    [page],
  )

  // Reserve min height only when there are no rows to show (initial load / empty result), so the
  // overlay has room. When previous rows are kept during a refetch, keep auto height and let the
  // LinearProgress overlay sit on top instead of collapsing the grid.
  const showMinHeight = invoices.length === 0

  return (
    <DataGridPremium
      initialState={{
        sorting: {
          sortModel: [{ field: 'no', sort: 'desc' }],
        },
      }}
      sx={{
        height: showMinHeight ? 300 : 'auto',
        '.MuiDataGrid-overlayWrapperInner': { height: '89px !important' },
      }}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      rowCount={rowCount}
      pageSizeOptions={[10, 25, 50, 100]}
      filterMode="server"
      getRowHeight={() => 'auto'}
      onFilterModelChange={handleFilterChange}
      getDetailPanelContent={({ row }) => (
        <FigmaBox fullWidth fullPadding spacing={Spacings.large}>
          <InvoiceDetails invoice={row} />
        </FigmaBox>
      )}
      getDetailPanelHeight={() => 'auto'}
      filterModel={filterModel}
      loading={isLoading}
      apiRef={apiRef}
      headerFilters
      disableColumnFilter
      slotProps={{
        headerFilterCell: { showClearIcon: true },
      }}
      slots={{
        headerFilterMenu: null,
        loadingOverlay: () => <LinearProgress />,
        noRowsOverlay: () => (
          <Stack
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            No invoices found
          </Stack>
        ),
      }}
      rows={invoices}
      columnHeaderHeight={64}
      // Without an explicit height, columns with no filter (Customer, Remaining, Actions) fall back to a
      // shorter default row height than columns that do render a filter control, leaving the header
      // filter row visibly uneven.
      headerFilterHeight={81}
      columns={columns}
    />
  )
}

type ActionsColumnProps = {
  invoice: Invoice
  actions: (invoice: Invoice, onClose?: () => unknown) => React.ReactNode
}

const ActionsColumn = ({ invoice, actions }: ActionsColumnProps) => {
  const ref = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  return (
    <>
      <IconButton data-testid="more-actions" onClick={() => setOpen(true)} ref={ref}>
        <MoreVertIcon />
      </IconButton>
      <ActionsMenu onClose={() => setOpen(false)} open={open} anchor={ref.current} actions={actions} invoice={invoice} />
    </>
  )
}

type ActionsMenuProps = {
  invoice: Invoice
  open: boolean
  actions: (invoice: Invoice, onClose?: () => unknown) => React.ReactNode
  onClose?: () => unknown
  anchor: HTMLButtonElement | null
}

const ActionsMenu = ({ actions, invoice, open, onClose, anchor }: ActionsMenuProps) => (
  <Menu open={Boolean(open)} anchorEl={anchor} onClose={onClose}>
    <Paper sx={{ width: 325, maxWidth: '100%' }}>
      <MenuList>{actions(invoice, onClose)}</MenuList>
    </Paper>
  </Menu>
)
